import { MenuAction } from '@anecdotes/design-system/dropdown-menu';
import {
  CompanyResponse,
  TrustCenterFacadeService,
  TrustCenterSharedModalsService,
} from '@anecdotes/shared-trust-center';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { RegularDateFormatMMMdy } from '@anecdotes/helpers';
import { TrustCenterModalService } from '../../../../services/trustcenter-modals/trustcenter-modals.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@anecdotes/design-system/alert';

@Component({
  selector: 'app-company-item',
  templateUrl: './company-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyItemComponent implements OnInit {
  @HostBinding('class')
  private readonly classes = 'w-full h-14 bg-white hover:bg-navy-10 cursor-pointer';

  readonly dateFormat = RegularDateFormatMMMdy;

  menuActions: MenuAction[];
  menuActionLoader$ = new BehaviorSubject<boolean>(false);
  company$: Observable<CompanyResponse>;
  autoApprovalEnabled: boolean;

  @Input()
  companyId: number;

  @Input()
  userCount: number;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly trustCenterFacadeService: TrustCenterFacadeService,
    private readonly trustCenterModalService: TrustCenterModalService,
    private readonly trustCenterSharedModalsService: TrustCenterSharedModalsService,
    private readonly alertService: AlertService,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.company$ = this.trustCenterFacadeService.getCompanyById(this.companyId);

    this.company$.pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe((company) => {
      this.autoApprovalEnabled = company.automatic_approval;

      this.menuActions = [
        {
          icon: 'edit',
          translationKey: this.buildTranslationKey('editCompany'),
          action: () => this.openEditCompanySidePanel(),
        },
        {
          icon: 'Resolve2',
          translationKey: this.buildTranslationKey('approveAllUsers'),
          disabled: !company.is_active,
          action: () => this.approveRevokeAllUsers(true),
        },
        {
          icon: 'X',
          translationKey: this.buildTranslationKey('revokeAllUsers'),
          disabled: !company.is_active,
          action: () => this.approveRevokeAllUsers(false),
        },
        {
          icon: 'remove',
          translationKey: this.buildTranslationKey('deleteCompany'),
          action: () => this.deleteCompany(),
        },
      ];
      this.cd.detectChanges();
    });
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.company.${key}`;
  }

  async updateAutoApproval(autoApprovalEnabled: boolean): Promise<void> {
    const previousValue = this.autoApprovalEnabled;
    this.autoApprovalEnabled = autoApprovalEnabled;
    this.cd.detectChanges();

    try {
      await this.trustCenterFacadeService.updateCompany(this.companyId, {
        automatic_approval: autoApprovalEnabled,
      });
    } catch {
      this.autoApprovalEnabled = previousValue;
      this.cd.detectChanges();
    }
  }

  private async approveRevokeAllUsers(approve: boolean): Promise<void> {
    this.menuActionLoader$.next(true);

    try {
      if (approve) {
        await this.trustCenterFacadeService.approveAllCompanyUsers(this.companyId);
      } else {
        await this.trustCenterFacadeService.revokeAllCompanyUsers(this.companyId);
      }
    } catch {
      this.alertService.openActionErrorAlert({
        customBaseTranslationKey: this.buildTranslationKey('actionError'),
      });
    } finally {
      this.menuActionLoader$.next(false);
    }
  }

  private async openEditCompanySidePanel(): Promise<void> {
    this.trustCenterSharedModalsService.openAddEditCompanySidePanel(await firstValueFrom(this.company$));
  }

  private async deleteCompany(): Promise<void> {
    if (await this.trustCenterSharedModalsService.openDeleteCompanyConfirmationModal()) {
      this.menuActionLoader$.next(true);

      try {
        await this.trustCenterFacadeService.deleteCompany(this.companyId);
      } catch {
        this.alertService.openActionErrorAlert({
          customBaseTranslationKey: this.buildTranslationKey('deleteConfirmationModal.tryAgainError'),
        });
      } finally {
        this.menuActionLoader$.next(false);
      }
    }
  }

  @HostListener('click')
  private async onClick(): Promise<void> {
    this.trustCenterModalService.openCompanySidePanel(await firstValueFrom(this.company$));
  }
}
